export type TaskPriority = 'urgent' | 'high' | 'medium' | 'low';
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'done';

export interface TaskUser {
  id: number;
  name: string;
  initials: string;
  avatarColor: string;
}

export interface StatusHistoryEntry {
  from: TaskStatus;
  to: TaskStatus;
  changedBy: TaskUser;
  changedAt: string;
  reason?: string;
}

export interface TaskComment {
  id: string;
  author: TaskUser;
  text: string;
  createdAt: string;
}

export type TaskTeam = 'Engineering' | 'Finance' | 'Operations' | 'HR' | 'Sales' | 'KAM' | 'Management';

export const TEAMS: TaskTeam[] = ['Engineering', 'Finance', 'Operations', 'HR', 'Sales', 'KAM', 'Management'];

export const TEAM_CONFIG: Record<TaskTeam, { bg: string; color: string }> = {
  Engineering:  { bg: 'bg-indigo-50',  color: 'text-indigo-700'  },
  Finance:      { bg: 'bg-emerald-50', color: 'text-emerald-700' },
  Operations:   { bg: 'bg-amber-50',   color: 'text-amber-700'   },
  HR:           { bg: 'bg-rose-50',    color: 'text-rose-700'    },
  Sales:        { bg: 'bg-purple-50',  color: 'text-purple-700'  },
  KAM:          { bg: 'bg-cyan-50',    color: 'text-cyan-700'    },
  Management:   { bg: 'bg-gray-100',   color: 'text-gray-700'    },
};

export interface Task {
  id: string;
  key: string;
  title: string;
  description: string;
  assignee: TaskUser;
  reporter: TaskUser;
  priority: TaskPriority;
  status: TaskStatus;
  team: TaskTeam;
  dueDate: string | null;
  labels: string[];
  createdAt: string;
  updatedAt: string;
  statusHistory: StatusHistoryEntry[];
  comments: TaskComment[];
}

export interface Column {
  id: TaskStatus;
  title: string;
  color: string;
  dotColor: string;
}

export const COLUMNS: Column[] = [
  { id: 'backlog', title: 'Backlog', color: 'text-gray-500', dotColor: 'bg-gray-400' },
  { id: 'todo', title: 'To Do', color: 'text-blue-600', dotColor: 'bg-blue-500' },
  { id: 'in_progress', title: 'In Progress', color: 'text-amber-600', dotColor: 'bg-amber-500' },
  { id: 'done', title: 'Done', color: 'text-emerald-600', dotColor: 'bg-emerald-500' },
];

export const ACTIVE_COLUMNS: Column[] = COLUMNS.filter(c => c.id !== 'backlog');
export const BACKLOG_COLUMN = COLUMNS.find(c => c.id === 'backlog')!;

export const PRIORITY_CONFIG: Record<
  TaskPriority,
  { label: string; color: string; bg: string; border: string }
> = {
  urgent: {
    label: 'Urgent',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
  },
  high: {
    label: 'High',
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
  },
  medium: {
    label: 'Medium',
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
  },
  low: {
    label: 'Low',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
  },
};

export interface SortConfig {
  field: 'createdAt' | 'updatedAt' | 'dueDate' | 'priority' | 'title';
  direction: 'asc' | 'desc';
}

export interface BoardViewUser {
  id: number;
  name: string;
  initials: string;
  avatarColor: string;
}
